(() => {
  const EVENT_OPTIONS = { once: true };
  const ACTIVATION_EVENTS = ['pointerenter', 'focusin', 'touchstart'];

  const playSilently = (videoEl) => {
    if (!videoEl) return;

    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.autoplay = true;
    videoEl.loop = true;

    const attemptPlay = () => {
      try {
        videoEl.play();
      } catch (error) {
        console.error('[UGC Gallery] Failed to begin autoplay', error);
      }
    };

    if (videoEl.readyState >= 2) {
      attemptPlay();
    } else {
      videoEl.addEventListener('loadeddata', attemptPlay, EVENT_OPTIONS);
    }
  };

  const enableControls = (videoEl) => {
    if (!videoEl) return;
    videoEl.controls = true;
    videoEl.setAttribute('preload', 'auto');
  };

  class UGCGalleryVideo extends HTMLElement {
    constructor() {
      super();
      this._initialised = false;
      this._fullLoaded = false;
    }

    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.hydratePreview();

      ACTIVATION_EVENTS.forEach((eventName) => {
        this.addEventListener(
          eventName,
          () => {
            this.activateFullVideo();
          },
          EVENT_OPTIONS
        );
      });
    }

    getPreviewVideo() {
      return this.querySelector('video-responsive video') || this.querySelector('video');
    }

    hydratePreview() {
      playSilently(this.getPreviewVideo());
    }

    activateFullVideo() {
      if (this._fullLoaded) return;

      const template = this.querySelector('template[data-ugc-video-full]');
      if (!template) {
        const previewVideo = this.getPreviewVideo();
        enableControls(previewVideo);
        this._fullLoaded = true;
        this.dataset.fullLoaded = 'true';
        return;
      }

      const previewElement = this.querySelector('video-responsive');
      const fallbackVideo = previewElement ? null : this.querySelector('video');
      const clone = template.content.firstElementChild
        ? template.content.firstElementChild.cloneNode(true)
        : null;

      if (!clone) {
        this._fullLoaded = true;
        this.dataset.fullLoaded = 'true';
        return;
      }

      let finalised = false;
      const revealClone = () => {
        if (finalised) return;
        finalised = true;

        clone.style.visibility = '';
        clone.style.position = '';
        clone.style.pointerEvents = '';

        if (previewElement && previewElement.isConnected) {
          previewElement.replaceWith(clone);
        } else if (fallbackVideo && fallbackVideo.isConnected) {
          fallbackVideo.replaceWith(clone);
        } else if (!clone.isConnected) {
          this.insertBefore(clone, template);
        }

        template.remove();
        this._fullLoaded = true;
        this.dataset.fullLoaded = 'true';

        const fullVideo = this.getPreviewVideo();
        enableControls(fullVideo);
        playSilently(fullVideo);
      };

      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      clone.style.position = 'absolute';
      clone.style.inset = '0';

      if (previewElement) {
        previewElement.insertAdjacentElement('afterend', clone);
      } else if (fallbackVideo) {
        fallbackVideo.insertAdjacentElement('afterend', clone);
      } else {
        this.insertBefore(clone, template);
      }

      const targetVideo = clone.matches('video')
        ? clone
        : clone.querySelector('video');

      if (!targetVideo) {
        revealClone();
        return;
      }

      const cleanup = () => {
        targetVideo.removeEventListener('loadeddata', onReady, EVENT_OPTIONS);
        targetVideo.removeEventListener('error', onReady, EVENT_OPTIONS);
      };

      const onReady = () => {
        cleanup();
        revealClone();
      };

      if (targetVideo.readyState >= 2) {
        revealClone();
      } else {
        targetVideo.addEventListener('loadeddata', onReady, EVENT_OPTIONS);
        targetVideo.addEventListener('error', onReady, EVENT_OPTIONS);
        targetVideo.load?.();
      }
    }
  }

  const upgradeLegacyNodes = (scope = document) => {
    if (!scope) return;

    const legacyNodes = scope.querySelectorAll('[data-ugc-video]');
    if (!legacyNodes.length) return;

    legacyNodes.forEach((legacy) => {
      if (legacy instanceof UGCGalleryVideo) return;

      const upgraded = document.createElement('ugc-gallery-video');

      upgraded.className = legacy.className;
      legacy.getAttributeNames().forEach((attrName) => {
        if (attrName === 'data-ugc-video') return;
        upgraded.setAttribute(attrName, legacy.getAttribute(attrName));
      });

      while (legacy.firstChild) {
        upgraded.appendChild(legacy.firstChild);
      }

      legacy.replaceWith(upgraded);
    });
  };

  if (!customElements.get('ugc-gallery-video')) {
    customElements.define('ugc-gallery-video', UGCGalleryVideo);
  }

  document.addEventListener('DOMContentLoaded', () => upgradeLegacyNodes(), EVENT_OPTIONS);
  document.addEventListener('shopify:section:load', (event) => upgradeLegacyNodes(event.target));
  document.addEventListener('shopify:section:select', (event) => upgradeLegacyNodes(event.target));

  window.UGCGalleryVideoInit = upgradeLegacyNodes;
})();

